import { useApi } from '@/api';
import type { GetSalesTransactionsResponse } from '@/api/handlers/resources/sales/responses/GetSalesTransactionsResponse';
import type { ApiResponse } from '@/api/handlers/responses/ApiResponse';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type UseSalesTransactionsPageQueryParams = {
    from: string;
    to: string;
    page: number;
    pageSize: number;
    sellerName?: string;
    transactionType?: 'Cash' | 'Cashless';
};

/**
 * Single-page query for the transactions table's **server-side** pagination.
 * Fetches exactly one page so a day with thousands of sales is never loaded at once — the table
 * jumps between pages and the rows-per-page control re-keys the query.
 *
 * `placeholderData: keepPreviousData` keeps the current page visible while the
 * next one loads, so paging doesn't flash the skeleton. The `queryKey` carries
 * the window + page + size so any change refetches.
 */
export const useSalesTransactionsPageQuery = (
    params: UseSalesTransactionsPageQueryParams,
    enabled = true,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetSalesTransactionsResponse>>({
        queryKey: [
            apiClient.sales.getSalesTransactions.name,
            'page',
            params.from,
            params.to,
            params.page,
            params.pageSize,
            params.sellerName ?? '',
            params.transactionType ?? '',
        ],
        queryFn: () =>
            apiClient.sales.getSalesTransactions({
                from: params.from,
                to: params.to,
                page: params.page,
                pageSize: params.pageSize,
                sellerName: params.sellerName,
                transactionType: params.transactionType,
            }),
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
};
